import React, { useEffect, useState } from 'react';
import { Table, Button, Tag, Space, Input, DatePicker, Select, message, Modal, Descriptions, Divider, Tooltip, Typography, Dropdown, Empty, Spin } from 'antd';
import { EyeOutlined, ReloadOutlined, SearchOutlined, ShoppingCartOutlined, PlusOutlined, ExclamationCircleOutlined, EditOutlined, InboxOutlined, DownOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { getAllPedidos, searchPedidos, updatePedido, updatePedidoStatus, cancelPedido, getPedidoById } from '../services/pedidosService';
import { getAllSuppliers } from '../services/supplierService';
import OrderCreationModal from '../components/orders/OrderCreationModal';
import OrderAttachments from '../components/orders/OrderAttachments';
import OrderItemsEditModal from '../components/orders/OrderItemsEditModal';
import OrdersSummaryCards from '../components/orders/OrdersSummaryCards';
import SupplierOrdersModal from '../components/orders/SupplierOrdersModal';
import OrderPaymentModal from '../components/orders/OrderPaymentModal';
import OrderRowActions from '../components/orders/OrderRowActions';
import './ordenesCompraPage.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OrdenesCompraPage = () => {
    const { user } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    
    // Summary data from API
    const [summary, setSummary] = useState(null);

    // Modal state
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    
    // Edit items modal state
    const [editItemsModalVisible, setEditItemsModalVisible] = useState(false);
    const [orderForEdit, setOrderForEdit] = useState(null);
    
    // Supplier orders modal state
    const [supplierModalVisible, setSupplierModalVisible] = useState(false);
    const [supplierModalPreselectedId, setSupplierModalPreselectedId] = useState(null);

    // Supplier dropdown (in the "Ver por Proveedor" button) state
    const [supplierDropdownVisible, setSupplierDropdownVisible] = useState(false);
    const [suppliersForDropdown, setSuppliersForDropdown] = useState([]);
    const [loadingSuppliersForDropdown, setLoadingSuppliersForDropdown] = useState(false);
    const [supplierDropdownSearch, setSupplierDropdownSearch] = useState('');

    // Payment modal state
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [orderForPayment, setOrderForPayment] = useState(null);

    // Inline editing state
    const [editingCell, setEditingCell] = useState({ id: null, field: null });
    const [editValue, setEditValue] = useState('');

    // Filters
    const [filters, setFilters] = useState({
        status: null,
        dateFrom: null,
        dateTo: null,
        search: null,
    });

    // Fetch orders from backend
    const fetchOrders = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const response = await getAllPedidos(page, pageSize, filters);

            if (response && response.success) {
                const ordersList = response.data.orders || response.data.pedidos || [];
                ordersList.sort((a, b) => (b.order_number || '').localeCompare(a.order_number || ''));
                setOrders(ordersList);
                
                // Extract summary data
                if (response.data.summary) {
                    setSummary(response.data.summary);
                }
                
                setPagination({
                    current: response.data.pagination?.page || page,
                    pageSize: response.data.pagination?.limit || pageSize,
                    total: response.data.pagination?.total || ordersList.length,
                });
            } else {
                message.error('Error al cargar órdenes de compra');
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            message.error('Error al cargar órdenes del servidor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    // Handle table pagination change
    const handleTableChange = (paginationConfig) => {
        fetchOrders(paginationConfig.current, paginationConfig.pageSize);
    };

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        fetchOrders(1, pagination.pageSize);
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({
            status: null,
            dateFrom: null,
            dateTo: null,
            search: null,
        });
        // Fetch without filters
        setTimeout(() => fetchOrders(1, pagination.pageSize), 100);
    };

    // Lazy-load suppliers for the "Ver por Proveedor" dropdown (cached in state)
    const loadSuppliersForDropdown = async () => {
        setLoadingSuppliersForDropdown(true);
        try {
            const response = await getAllSuppliers(1, 500, {});
            if (response && response.success) {
                setSuppliersForDropdown(response.suppliers || []);
            } else {
                message.error('Error al cargar proveedores');
            }
        } catch (error) {
            console.error('Error fetching suppliers for dropdown:', error);
            message.error('Error al cargar proveedores');
        } finally {
            setLoadingSuppliersForDropdown(false);
        }
    };

    const handleSupplierPickedFromDropdown = (supplierId) => {
        setSupplierDropdownVisible(false);
        setSupplierModalPreselectedId(supplierId);
        setSupplierModalVisible(true);
    };

    // View order details
    const handleViewOrder = async (orderId) => {
        setLoadingDetails(true);
        setDetailsModalVisible(true);
        try {
            const response = await getPedidoById(orderId);
            console.log('Full API Response:', response);
            console.log('Response.data:', response?.data);
            
            if (response && response.success && response.data) {
                // Backend returns { success: true, data: { order: {...}, items: [...] } }
                const orderData = response.data.order || response.data;
                const items = response.data.items || orderData.items || [];
                
                console.log('Extracted Order Data:', orderData);
                console.log('Extracted Items:', items);
                
                // Merge items into order data
                setSelectedOrder({
                    ...orderData,
                    items: items
                });
            } else if (response && response.data) {
                // Handle case where response doesn't have success flag but has data
                const orderData = response.data.order || response.data;
                const items = response.data.items || orderData.items || [];
                setSelectedOrder({
                    ...orderData,
                    items: items
                });
            } else {
                message.error('Error al cargar detalles de la orden');
                setDetailsModalVisible(false);
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
            message.error('Error al cargar detalles de la orden');
            setDetailsModalVisible(false);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Handle order creation
    const handleOrderCreated = (newOrder) => {
        message.success(`Orden de compra "${newOrder?.order_number || 'Nueva'}" creada exitosamente`);
        fetchOrders(pagination.current, pagination.pageSize);
    };

    // Handle cancel order
    const handleCancelOrder = (record) => {
        Modal.confirm({
            title: '¿Cancelar Orden?',
            icon: <ExclamationCircleOutlined />,
            content: `¿Desea cancelar la orden de compra "${record.order_number}"? Esta acción no se puede deshacer.`,
            okText: 'Sí, cancelar orden',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const response = await cancelPedido(record.id, 'Cancelado por el usuario');
                    if (response.success) {
                        message.success('Orden de compra cancelada exitosamente');
                        fetchOrders(pagination.current, pagination.pageSize);
                    }
                } catch (error) {
                    console.error('Error cancelling order:', error);
                    message.error(error.response?.data?.message || 'Error al cancelar la orden de compra');
                }
            },
        });
    };

    // Handle edit order items - open edit modal
    const handleEditOrderItems = async (record) => {
        // Fetch full order details including items
        setLoadingDetails(true);
        try {
            const response = await getPedidoById(record.id);
            if (response && response.success && response.data) {
                const orderData = response.data.order || response.data;
                const items = response.data.items || orderData.items || [];
                setOrderForEdit({ ...orderData, items });
                setEditItemsModalVisible(true);
            } else {
                message.error('Error al cargar detalles de la orden para edición');
            }
        } catch (error) {
            console.error('Error fetching order for edit:', error);
            message.error('Error al cargar la orden');
        } finally {
            setLoadingDetails(false);
        }
    };

    // Handle edit modal saved - refresh data
    const handleEditItemsSaved = (updatedOrder) => {
        fetchOrders(pagination.current, pagination.pageSize);
        // If details modal was open, refresh the selected order
        if (detailsModalVisible && selectedOrder?.id === updatedOrder?.id) {
            handleViewOrder(updatedOrder.id);
        }
    };

    // Check if order can be edited based on status
    const canEditOrder = (status) => {
        return status === 'PENDIENTE'
            || status === 'APROBADO'
            || status === 'RECIBIDO'
            || status === 'EN_TRANSITO'
            || status === 'PARCIAL';
    };

    // Check if user has admin role
    const isAdmin = user?.role === 'ClientAdministrator' || user?.role === 'admin';

    // Handle inline cell edit start
    const handleCellEdit = (record, field) => {
        setEditingCell({ id: record.id, field });
        setEditValue(record[field] || '');
    };

    // Handle inline cell edit save
    const handleCellSave = async (record, field) => {
        if (editValue === record[field]) {
            setEditingCell({ id: null, field: null });
            return;
        }

        // Special handling for status change to CANCELADO
        if (field === 'status' && editValue === 'CANCELADO') {
            setEditingCell({ id: null, field: null });
            Modal.confirm({
                title: '¿Cancelar Orden?',
                icon: <ExclamationCircleOutlined />,
                content: `¿Desea cancelar la orden de compra "${record.order_number}"? Esta acción no se puede deshacer.`,
                okText: 'Sí, cancelar orden',
                okType: 'danger',
                cancelText: 'No',
                onOk: async () => {
                    try {
                        const response = await cancelPedido(record.id, 'Cancelado por el usuario');
                        if (response.success) {
                            message.success('Orden de compra cancelada exitosamente');
                            fetchOrders(pagination.current, pagination.pageSize);
                        }
                    } catch (error) {
                        console.error('Error cancelling order:', error);
                        message.error(error.response?.data?.message || 'Error al cancelar la orden de compra');
                    }
                },
            });
            return;
        }

        try {
            let response;
            if (field === 'status') {
                response = await updatePedidoStatus(record.id, editValue);
            } else {
                response = await updatePedido(record.id, { [field]: editValue });
            }

            if (response.success) {
                // Enhanced message for RECIBIDO status with stock and caja info
                if (field === 'status' && editValue === 'RECIBIDO' && response.data?.order) {
                    const order = response.data.order;
                    const stockCount = order.stock_updated_count || 0;
                    const cajaCreated = order.caja_movement_created === 1;
                    
                    let msgParts = ['Orden recibida.'];
                    if (stockCount > 0) {
                        msgParts.push(`Stock +${stockCount} producto${stockCount > 1 ? 's' : ''}.`);
                    }
                    if (cajaCreated) {
                        msgParts.push('EGRESO registrado en Caja.');
                    }
                    message.success(msgParts.join(' '));
                } else {
                    message.success('Orden actualizada exitosamente');
                }
                fetchOrders(pagination.current, pagination.pageSize);
            }
        } catch (error) {
            console.error('Error updating order:', error);
            message.error(error.response?.data?.message || 'Error al actualizar la orden');
        } finally {
            setEditingCell({ id: null, field: null });
        }
    };

    // Handle inline cell edit cancel
    const handleCellCancel = () => {
        setEditingCell({ id: null, field: null });
        setEditValue('');
    };

    // Format price
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return 'N/A';
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Get status color
    const getStatusColor = (status) => {
        const statusColors = {
            'PENDIENTE': 'orange',
            'APROBADO': 'blue',
            'EN_TRANSITO': 'cyan',
            'RECIBIDO': 'green',
            'CANCELADO': 'red',
        };
        return statusColors[status] || 'default';
    };

    // Get status label
    const getStatusLabel = (status) => {
        const statusLabels = {
            'PENDIENTE': 'Pendiente',
            'APROBADO': 'Aprobado',
            'EN_TRANSITO': 'En Tránsito',
            'RECIBIDO': 'Recibido',
            'CANCELADO': 'Cancelado',
        };
        return statusLabels[status] || status;
    };

    // Payment date type label/color helpers
    const getPaymentDateTypeConfig = (type) => {
        const config = {
            'ADELANTADO':    { label: '⚡ Adelantado',    color: 'purple' },
            'CONTRAENTREGA': { label: '🚚 Contraentrega', color: 'blue'   },
            'A_DETERMINAR':  { label: '📅 A Determinar',  color: 'default' },
        };
        return config[type] || { label: type || '-', color: 'default' };
    };

    // Payment status badge helpers
    const getPaymentStatusConfig = (pStatus) => {
        const config = {
            'PENDIENTE': { label: 'Sin pagar', color: 'default'  },
            'PARCIAL':   { label: 'Parcial',   color: 'purple'   },
            'PAGADO':    { label: 'Pagado',    color: 'green'    },
        };
        return config[pStatus] || { label: pStatus || '-', color: 'default' };
    };

    // Table columns
    const columns = [
        {
            title: 'Núm.',
            dataIndex: 'order_number',
            key: 'order_number',
            width: 60,
            render: (text) => {
                const last4 = text ? text.slice(-4) : '-';
                return <strong title={text}>{last4}</strong>;
            },
        },
        {
            title: 'Prov.',
            dataIndex: 'supplier_razon_social',
            key: 'supplier_razon_social',
            width: 170,
            ellipsis: true,
        },
        {
            title: 'F. Orden',
            dataIndex: 'order_date',
            key: 'order_date',
            width: 95,
            render: (date) => {
                if (!date) return 'N/A';
                const dateStr = String(date).split('T')[0];
                const [year, month, day] = dateStr.split('-');
                return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
            },
        },
        {
            title: 'F. Entrega',
            dataIndex: 'expected_delivery_date',
            key: 'expected_delivery_date',
            width: 95,
            render: (date) => {
                if (!date) return '-';
                const dateStr = String(date).split('T')[0];
                const [year, month, day] = dateStr.split('-');
                return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
            },
        },
        {
            title: 'Subtotal',
            dataIndex: 'subtotal',
            key: 'subtotal',
            width: 120,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'IVA',
            dataIndex: 'iva',
            key: 'iva',
            width: 115,
            align: 'right',
            render: (iva) => formatPrice(iva),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 125,
            align: 'right',
            render: (price) => <strong>{formatPrice(price)}</strong>,
        },
        {
            title: 'T. Pago',
            dataIndex: 'payment_date_type',
            key: 'payment_date_type',
            width: 130,
            render: (type) => {
                const { label, color } = getPaymentDateTypeConfig(type);
                return <Tag color={color}>{label}</Tag>;
            },
        },
        {
            title: 'Envío',
            key: 'envio_status',
            width: 115,
            render: (_, record) => {
                const { status } = record;
                const isEditing = editingCell.id === record.id && editingCell.field === 'status';
                const isDisabled = status === 'CANCELADO' || status === 'RECIBIDO';

                if (isEditing) {
                    return (
                        <Select
                            value={editValue}
                            onChange={setEditValue}
                            onBlur={() => handleCellSave(record, 'status')}
                            onPressEnter={() => handleCellSave(record, 'status')}
                            style={{ width: '100%' }}
                            size="small"
                            autoFocus
                        >
                            <Option value="PENDIENTE">Pendiente</Option>
                            <Option value="APROBADO">Aprobado</Option>
                            <Option value="EN_TRANSITO">En Tránsito</Option>
                            <Option value="RECIBIDO">Recibido</Option>
                            <Option value="CANCELADO">Cancelado</Option>
                        </Select>
                    );
                }

                return (
                    <Tag
                        color={getStatusColor(status)}
                        onClick={() => !isDemoMode && !isDisabled && handleCellEdit(record, 'status')}
                        style={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.6 : 1,
                            margin: 0,
                            minWidth: 95,
                            textAlign: 'center',
                        }}
                        title={isDisabled ? 'No se puede modificar' : 'Haz clic para editar'}
                    >
                        {getStatusLabel(status)}
                    </Tag>
                );
            },
        },
        {
            title: 'Pago',
            key: 'payment_status',
            width: 100,
            render: (_, record) => {
                const { status } = record;
                const { label: payLabel, color: payColor } = getPaymentStatusConfig(record.payment_status);
                const paid = Number(record.paid_amount) || 0;
                const paidTitle = paid > 0
                    ? `Pagado: $ ${paid.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : undefined;

                return (
                    <Tooltip title={paidTitle || (status !== 'CANCELADO' ? 'Registrar pago' : undefined)}>
                        <Tag
                            color={payColor}
                            style={{ margin: 0, minWidth: 80, textAlign: 'center', cursor: status !== 'CANCELADO' ? 'pointer' : 'default' }}
                            onClick={() => {
                                if (status === 'CANCELADO') return;
                                if (isDemoMode) { message.info('Registrar pago — disponible en la versión completa del sistema'); return; }
                                setOrderForPayment(record);
                                setPaymentModalVisible(true);
                            }}
                        >
                            {payLabel}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 165,
            fixed: 'right',
            render: (_, record) => (
                <OrderRowActions
                    order={record}
                    isAdmin={isAdmin}
                    canEditOrder={canEditOrder}
                    getStatusLabel={getStatusLabel}
                    onView={(o) => handleViewOrder(o.id)}
                    onEdit={handleEditOrderItems}
                    onCancel={handleCancelOrder}
                    onRegisterPayment={(o) => {
                        setOrderForPayment(o);
                        setPaymentModalVisible(true);
                    }}
                />
            ),
        },
    ];

    return (
        <div className="ordenes-page-container">
            <div className="ordenes-header">
                <h1>
                    <ShoppingCartOutlined />
                    Órdenes de Compra
                </h1>
                <Space>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            if (isDemoMode) {
                                message.info('Nueva Orden de Compra — disponible en la versión completa del sistema');
                                return;
                            }
                            setCreateModalVisible(true);
                        }}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                    >
                        Nueva Orden de Compra
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchOrders(pagination.current, pagination.pageSize)}
                    >
                        Actualizar
                    </Button>
                </Space>
            </div>
            
            {/* Summary Cards */}
            {summary && <OrdersSummaryCards summary={summary} orders={orders} loading={loading} isDemoMode={isDemoMode} />}

            {/* Filters */}
            <div className="ordenes-filters">
                <Space wrap size="middle">
                    <Select
                        placeholder="Filtrar por envío"
                        style={{ width: 200 }}
                        allowClear
                        value={filters.status}
                        onChange={(value) => handleFilterChange('status', value)}
                        disabled={isDemoMode}
                    >
                        <Option value="PENDIENTE">Pendiente</Option>
                        <Option value="APROBADO">Aprobado</Option>
                        <Option value="EN_TRANSITO">En Tránsito</Option>
                        <Option value="RECIBIDO">Recibido</Option>
                        <Option value="CANCELADO">Cancelado</Option>
                    </Select>

                    <Input
                        placeholder="Buscar por proveedor o número"
                        style={{ width: 250 }}
                        allowClear
                        prefix={<SearchOutlined />}
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        disabled={isDemoMode}
                    />

                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Fecha desde', 'Fecha hasta']}
                        disabled={isDemoMode}
                        onChange={(dates) => {
                            if (dates) {
                                handleFilterChange('dateFrom', dates[0].format('YYYY-MM-DD'));
                                handleFilterChange('dateTo', dates[1].format('YYYY-MM-DD'));
                            } else {
                                handleFilterChange('dateFrom', null);
                                handleFilterChange('dateTo', null);
                            }
                        }}
                    />

                    <Button type="primary" onClick={handleApplyFilters} disabled={isDemoMode}>
                        Aplicar Filtros
                    </Button>
                    <Button onClick={handleClearFilters} disabled={isDemoMode}>
                        Limpiar
                    </Button>
                    <Dropdown
                        trigger={['click']}
                        open={supplierDropdownVisible && !supplierModalVisible}
                        onOpenChange={(open) => {
                            if (isDemoMode) {
                                message.info('Vista de órdenes por proveedor — disponible en la versión completa del sistema');
                                return;
                            }
                            if (open && suppliersForDropdown.length === 0 && !loadingSuppliersForDropdown) {
                                loadSuppliersForDropdown();
                            }
                            setSupplierDropdownVisible(open);
                        }}
                        disabled={supplierModalVisible}
                        overlay={(() => {
                            const q = supplierDropdownSearch.trim().toLowerCase();
                            const filtered = q
                                ? suppliersForDropdown.filter((s) =>
                                    (s.razon_social || '').toLowerCase().includes(q) ||
                                    String(s.documento_nro || '').toLowerCase().includes(q)
                                )
                                : suppliersForDropdown;
                            return (
                                <div className="supplier-dropdown-panel">
                                    <Input
                                        placeholder="Buscar proveedor..."
                                        allowClear
                                        prefix={<SearchOutlined />}
                                        value={supplierDropdownSearch}
                                        onChange={(e) => setSupplierDropdownSearch(e.target.value)}
                                        autoFocus
                                    />
                                    <div className="supplier-dropdown-list">
                                        {loadingSuppliersForDropdown ? (
                                            <div className="supplier-dropdown-loading">
                                                <Spin />
                                            </div>
                                        ) : filtered.length === 0 ? (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="Sin coincidencias"
                                            />
                                        ) : (
                                            filtered.map((s) => (
                                                <div
                                                    key={s.id}
                                                    className="supplier-dropdown-item"
                                                    onClick={() => handleSupplierPickedFromDropdown(s.id)}
                                                >
                                                    <div className="supplier-dropdown-item-name">
                                                        {s.razon_social}
                                                    </div>
                                                    <div className="supplier-dropdown-item-cuit">
                                                        CUIT {s.documento_nro}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                    >
                        <Button
                            type="default"
                            icon={<InboxOutlined />}
                            style={{ borderColor: '#1890ff', color: '#1890ff' }}
                            disabled={isDemoMode}
                        >
                            Ver por Proveedor <DownOutlined />
                        </Button>
                    </Dropdown>
                </Space>
            </div>

            {/* Table */}
            <div className="ordenes-table-container">
                <Table
                    columns={columns}
                    dataSource={orders}
                    rowKey="id"
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    size="middle"
                />
            </div>

            {/* Order Creation Modal */}
            <OrderCreationModal
                visible={createModalVisible}
                onClose={() => setCreateModalVisible(false)}
                onOrderCreated={handleOrderCreated}
            />

            {/* Order Details Modal */}
            <Modal
                title={
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                        <EyeOutlined style={{ marginRight: '8px' }} />
                        Detalles de la Orden de Compra
                    </div>
                }
                open={detailsModalVisible}
                onCancel={() => {
                    setDetailsModalVisible(false);
                    setSelectedOrder(null);
                }}
                footer={[
                    selectedOrder && canEditOrder(selectedOrder.status) && isAdmin && (
                        <Button 
                            key="edit" 
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => {
                                setOrderForEdit(selectedOrder);
                                setEditItemsModalVisible(true);
                            }}
                            style={{ float: 'left' }}
                        >
                            Editar Items
                        </Button>
                    ),
                    <Button 
                        key="close" 
                        onClick={() => {
                            setDetailsModalVisible(false);
                            setSelectedOrder(null);
                        }}
                    >
                        Cerrar
                    </Button>
                ]}
                width={1000}
                loading={loadingDetails}
            >
                {selectedOrder && (
                    <div>
                        {/* Order Header Information */}
                        <Descriptions 
                            bordered 
                            size="small" 
                            column={2}
                            styles={{ label: { fontWeight: 'bold', backgroundColor: '#fafafa' } }}
                        >
                            <Descriptions.Item label="Número de Orden" span={1}>
                                <strong style={{ fontSize: '16px' }}>{selectedOrder.order_number}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Envío" span={1}>
                                <Tag color={getStatusColor(selectedOrder.status)}>
                                    {getStatusLabel(selectedOrder.status)}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Pago" span={1}>
                                {(() => {
                                    const { label, color } = getPaymentStatusConfig(selectedOrder.payment_status);
                                    const paid = Number(selectedOrder.paid_amount) || 0;
                                    const paidTitle = paid > 0
                                        ? `Pagado: $ ${paid.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                        : undefined;
                                    return (
                                        <Tooltip title={paidTitle}>
                                            <Tag color={color} style={{ margin: 0 }}>{label}</Tag>
                                        </Tooltip>
                                    );
                                })()}
                            </Descriptions.Item>
                            <Descriptions.Item label="Proveedor" span={2}>
                                {selectedOrder.supplier_razon_social || selectedOrder.supplier_name || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Fecha de Creación">
                                {selectedOrder.order_date ? new Date(selectedOrder.order_date).toLocaleDateString('es-AR') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Fecha de Entrega Estimada">
                                {selectedOrder.expected_delivery_date ? new Date(selectedOrder.expected_delivery_date).toLocaleDateString('es-AR') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Condición de Pago">
                                {selectedOrder.payment_terms || selectedOrder.payment_term || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Método de Pago">
                                {selectedOrder.payment_method || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Factura Proveedor" span={2}>
                                {selectedOrder.related_supplier_invoice || <span style={{ color: '#999' }}>No especificada</span>}
                            </Descriptions.Item>
                            {selectedOrder.discount > 0 && (
                                <Descriptions.Item label="Descuento Total" span={2}>
                                    <strong style={{ color: '#52c41a' }}>
                                        {formatPrice(selectedOrder.discount)}
                                    </strong>
                                </Descriptions.Item>
                            )}
                            {selectedOrder.notes && (
                                <Descriptions.Item label="Notas" span={2}>
                                    {selectedOrder.notes}
                                </Descriptions.Item>
                            )}
                            {selectedOrder.is_simplified && (
                                <Descriptions.Item label="Tipo de Orden" span={2}>
                                    <Tag color="purple">Simplificada</Tag>
                                    <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                                        Registrada desde totales de factura del proveedor
                                    </Typography.Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        {/* Order Attachments Section */}
                        <Divider orientation="left">Archivos Adjuntos</Divider>
                        <OrderAttachments 
                            orderId={selectedOrder.id}
                            readOnly={false}
                            canDelete={user?.role === 'ClientAdministrator' || user?.role === 'admin'}
                        />

                        <Divider orientation="left">Productos</Divider>

                        {/* Items section — behaviour depends on order type */}
                        {(!selectedOrder.items || selectedOrder.items.length === 0) && selectedOrder.is_simplified && (
                            <div style={{
                                padding: '20px 24px',
                                backgroundColor: '#f9f0ff',
                                border: '1px solid #d3adf7',
                                borderRadius: '4px',
                                marginBottom: '16px'
                            }}>
                                <div style={{ marginBottom: 12 }}>
                                    <Tag color="purple" style={{ fontSize: '13px', padding: '2px 8px' }}>Orden Simplificada</Tag>
                                    <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: '12px' }}>
                                        Los ítems no se detallan en órdenes simplificadas — se registra directamente desde la factura.
                                    </Typography.Text>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                    <div style={{ padding: '10px 14px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e8e8e8' }}>
                                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 2 }}>Subtotal</div>
                                        <div style={{ fontSize: '15px', fontWeight: 500 }}>{formatPrice(selectedOrder.subtotal)}</div>
                                    </div>
                                    <div style={{ padding: '10px 14px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e8e8e8' }}>
                                        <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 2 }}>IVA inscripto</div>
                                        <div style={{ fontSize: '15px', fontWeight: 500 }}>{formatPrice(selectedOrder.iva)}</div>
                                    </div>
                                    <div style={{ padding: '10px 14px', backgroundColor: '#f0e6ff', borderRadius: '4px', border: '1px solid #d3adf7' }}>
                                        <div style={{ fontSize: '11px', color: '#722ed1', marginBottom: 2 }}>Total</div>
                                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#722ed1' }}>{formatPrice(selectedOrder.total)}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {(!selectedOrder.items || selectedOrder.items.length === 0) && !selectedOrder.is_simplified && (
                            <div style={{ 
                                padding: '40px 20px', 
                                textAlign: 'center', 
                                backgroundColor: '#fff7e6', 
                                border: '1px dashed #ffa940',
                                borderRadius: '4px',
                                marginBottom: '16px'
                            }}>
                                <ExclamationCircleOutlined style={{ fontSize: '32px', color: '#fa8c16', marginBottom: '12px' }} />
                                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
                                    Detalles de productos no disponibles
                                </div>
                                <div style={{ color: '#8c8c8c' }}>
                                    El endpoint GET /orders/:id no está retornando los items de la orden.
                                    <br />
                                    Backend necesita incluir el array de items en la respuesta.
                                </div>
                                <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', textAlign: 'left' }}>
                                    <strong>Totales disponibles:</strong>
                                    <div style={{ marginTop: '8px' }}>Subtotal: {formatPrice(selectedOrder.subtotal)}</div>
                                    <div>IVA: {formatPrice(selectedOrder.iva)}</div>
                                    <div>Descuento: {formatPrice(selectedOrder.discount)}</div>
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff', marginTop: '8px' }}>
                                        Total: {formatPrice(selectedOrder.total)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Order Items Table */}
                        {selectedOrder.items && selectedOrder.items.length > 0 && (
                        <Table
                            dataSource={selectedOrder.items || []}
                            rowKey={(record) => `${record.product_id}-${record.id || Math.random()}`}
                            pagination={false}
                            size="small"
                            scroll={{ x: 950 }}
                            columns={[
                                {
                                    title: 'Producto',
                                    dataIndex: 'product_description',
                                    key: 'product_description',
                                    width: 200,
                                    render: (text, record) => (
                                        <div>
                                            <div><strong>{text || record.product_name || 'Sin descripción'}</strong></div>
                                            {record.product_code && (
                                                <div style={{ fontSize: '12px', color: '#888' }}>
                                                    Código: {record.product_code}
                                                </div>
                                            )}
                                        </div>
                                    ),
                                },
                                {
                                    title: 'Cantidad',
                                    dataIndex: 'quantity',
                                    key: 'quantity',
                                    width: 80,
                                    align: 'center',
                                },
                                {
                                    title: 'Costo Base de Datos',
                                    dataIndex: 'unit_price',
                                    key: 'unit_price',
                                    width: 130,
                                    align: 'right',
                                    render: (price) => formatPrice(price),
                                },
                                {
                                    title: 'Costo Compra',
                                    dataIndex: 'bulk_price',
                                    key: 'bulk_price',
                                    width: 120,
                                    align: 'right',
                                    render: (price, record) => {
                                        const basePrice = price || record.unit_price;
                                        return (
                                            <div>
                                                <div>{formatPrice(basePrice)}</div>
                                                {price && price !== record.unit_price && (
                                                    <div style={{ fontSize: '11px', color: '#1890ff' }}>
                                                        (Precio especial)
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    },
                                },
                                {
                                    title: 'Descuento %',
                                    dataIndex: 'discount_percentage',
                                    key: 'discount_percentage',
                                    width: 100,
                                    align: 'center',
                                    render: (discount, record) => {
                                        const effectiveDiscount = discount || selectedOrder.global_discount || 0;
                                        const hasCustomDiscount = record.has_custom_discount;
                                        return (
                                            <Tag color={hasCustomDiscount ? 'gold' : 'default'}>
                                                {effectiveDiscount}%
                                            </Tag>
                                        );
                                    },
                                },
                                {
                                    title: 'Precio c/Desc.',
                                    key: 'price_after_discount',
                                    width: 120,
                                    align: 'right',
                                    render: (_, record) => {
                                        const basePrice = record.bulk_price || record.unit_price;
                                        const discount = record.discount_percentage || selectedOrder.global_discount || 0;
                                        const priceAfterDiscount = basePrice * (1 - discount / 100);
                                        return formatPrice(priceAfterDiscount);
                                    },
                                },
                                {
                                    title: 'Subtotal',
                                    key: 'subtotal',
                                    width: 120,
                                    align: 'right',
                                    render: (_, record) => {
                                        const basePrice = record.bulk_price || record.unit_price;
                                        const discount = record.discount_percentage || selectedOrder.global_discount || 0;
                                        const priceAfterDiscount = basePrice * (1 - discount / 100);
                                        const subtotal = priceAfterDiscount * record.quantity;
                                        return formatPrice(subtotal);
                                    },
                                },
                            ]}
                            summary={(pageData) => {
                                let totalSubtotal = 0;
                                let totalDiscount = 0;
                                let totalBeforeDiscount = 0;
                                let totalIVA = 0;

                                pageData.forEach(item => {
                                    const basePrice = item.bulk_price || item.unit_price;
                                    const discount = item.discount_percentage || selectedOrder.global_discount || 0;
                                    const itemBeforeDiscount = basePrice * item.quantity;
                                    const priceAfterDiscount = basePrice * (1 - discount / 100);
                                    const subtotal = priceAfterDiscount * item.quantity;
                                    // Use actual item IVA percentage (default 0 if not set)
                                    const itemIvaPercent = Number(item.iva_percentage) || 0;
                                    const itemIVA = subtotal * (itemIvaPercent / 100);
                                    
                                    totalBeforeDiscount += itemBeforeDiscount;
                                    totalSubtotal += subtotal;
                                    totalIVA += itemIVA;
                                });

                                totalDiscount = totalBeforeDiscount - totalSubtotal;
                                // Use order-level cargas_impositivas_on flag
                                const cargasOn = selectedOrder.cargas_impositivas_on;
                                const effectiveIVA = cargasOn ? totalIVA : 0;
                                // Include tax adjustments from order
                                const percepcion = Number(selectedOrder.percepcion) || 0;
                                const iibbDirecto = Number(selectedOrder.iibb_directo) || 0;
                                const retencion = Number(selectedOrder.retencion) || 0;
                                const grandTotal = totalSubtotal + effectiveIVA + percepcion + iibbDirecto - retencion;

                                // Determine IVA label based on items
                                const hasMultipleIvaRates = pageData.some(item => (Number(item.iva_percentage) || 0) !== (Number(pageData[0]?.iva_percentage) || 0));
                                const singleIvaRate = pageData.length > 0 ? Number(pageData[0]?.iva_percentage) || 0 : 0;
                                const ivaLabel = hasMultipleIvaRates ? 'IVA (mixto)' : `IVA (${singleIvaRate}%)`;

                                return (
                                    <>
                                        <Table.Summary.Row style={{ backgroundColor: '#f0f0f0' }}>
                                            <Table.Summary.Cell index={0} colSpan={6}>
                                                <strong>Subtotal (sin IVA)</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <strong>{formatPrice(totalSubtotal)}</strong>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        {totalDiscount > 0 && (
                                            <Table.Summary.Row style={{ backgroundColor: '#f6ffed' }}>
                                                <Table.Summary.Cell index={0} colSpan={6}>
                                                    <strong style={{ color: '#52c41a' }}>
                                                        Ahorro total por descuentos
                                                    </strong>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1} align="right">
                                                    <strong style={{ color: '#52c41a' }}>
                                                        -{formatPrice(totalDiscount)}
                                                    </strong>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        )}
                                        <Table.Summary.Row style={{ backgroundColor: '#f0f0f0' }}>
                                            <Table.Summary.Cell index={0} colSpan={6}>
                                                <strong>{ivaLabel}</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <strong>{formatPrice(effectiveIVA)}</strong>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                        {(percepcion > 0 || iibbDirecto > 0 || retencion > 0) && (
                                            <>
                                                {percepcion > 0 && (
                                                    <Table.Summary.Row>
                                                        <Table.Summary.Cell index={0} colSpan={6}>
                                                            Percepción
                                                        </Table.Summary.Cell>
                                                        <Table.Summary.Cell index={1} align="right">
                                                            +{formatPrice(percepcion)}
                                                        </Table.Summary.Cell>
                                                    </Table.Summary.Row>
                                                )}
                                                {iibbDirecto > 0 && (
                                                    <Table.Summary.Row>
                                                        <Table.Summary.Cell index={0} colSpan={6}>
                                                            IIBB Directo
                                                        </Table.Summary.Cell>
                                                        <Table.Summary.Cell index={1} align="right">
                                                            +{formatPrice(iibbDirecto)}
                                                        </Table.Summary.Cell>
                                                    </Table.Summary.Row>
                                                )}
                                                {retencion > 0 && (
                                                    <Table.Summary.Row>
                                                        <Table.Summary.Cell index={0} colSpan={6}>
                                                            Retención
                                                        </Table.Summary.Cell>
                                                        <Table.Summary.Cell index={1} align="right">
                                                            -{formatPrice(retencion)}
                                                        </Table.Summary.Cell>
                                                    </Table.Summary.Row>
                                                )}
                                            </>
                                        )}
                                        <Table.Summary.Row style={{ backgroundColor: '#e6f7ff', fontSize: '16px' }}>
                                            <Table.Summary.Cell index={0} colSpan={6}>
                                                <strong>TOTAL</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <strong style={{ fontSize: '18px', color: '#1890ff' }}>
                                                    {formatPrice(grandTotal)}
                                                </strong>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </>
                                );
                            }}
                        />
                        )}
                    </div>
                )}
            </Modal>

            {/* Order Items Edit Modal */}
            <OrderItemsEditModal
                visible={editItemsModalVisible}
                onClose={() => {
                    setEditItemsModalVisible(false);
                    setOrderForEdit(null);
                }}
                onSaved={handleEditItemsSaved}
                order={orderForEdit}
                userRole={user?.role}
            />
            
            {/* Supplier Orders Modal */}
            <SupplierOrdersModal
                visible={supplierModalVisible}
                onClose={() => {
                    setSupplierModalVisible(false);
                    setSupplierModalPreselectedId(null);
                }}
                preSelectedSupplierId={supplierModalPreselectedId}
                onViewOrderDetails={handleViewOrder}
                onActionDone={() => fetchOrders(pagination.current, pagination.pageSize)}
            />

            {/* Payment Registration Modal */}
            <OrderPaymentModal
                visible={paymentModalVisible}
                order={orderForPayment}
                onClose={() => {
                    setPaymentModalVisible(false);
                    setOrderForPayment(null);
                }}
                onPaymentRegistered={() => fetchOrders(pagination.current, pagination.pageSize)}
            />
        </div>
    );
};

export default OrdenesCompraPage;
